import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatErr } from "@/lib/api";
import { Sparkles, ArrowRight } from "lucide-react";

export default function Signup() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({ clinic_name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await register({
        clinic_name: form.clinic_name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
      });
      if (u?.is_super_admin) {
        navigate("/admin");
      } else {
        navigate("/onboarding");
      }
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone-100 grid grid-cols-1 md:grid-cols-2">
      <div className="flex items-center justify-center px-6 py-16">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="signup-form">
          <Link to="/" className="flex items-center gap-2 mb-12">
            <Sparkles size={20} strokeWidth={1.5} className="text-forest-600" />
            <span className="font-heading text-2xl text-forest-700">Toothpix</span>
          </Link>
          <div className="overline mb-3">14-day free trial</div>
          <h1 className="font-heading text-4xl text-forest-700">Create your clinic</h1>
          <p className="text-sm text-forest-400 mt-2">
            Already a member?{" "}
            <Link to="/login" data-testid="to-login" className="text-forest-600 underline underline-offset-4">
              Sign in
            </Link>
          </p>

          <div className="mt-10 space-y-5">
            <Field
              label="Clinic name"
              value={form.clinic_name}
              onChange={(v) => setForm({ ...form, clinic_name: v })}
              testid="signup-clinic-name"
              required
            />
            <Field
              label="Work email"
              type="email"
              value={form.email}
              onChange={(v) => setForm({ ...form, email: v })}
              testid="signup-email"
              required
            />
            <Field
              label="Password"
              type="password"
              value={form.password}
              onChange={(v) => setForm({ ...form, password: v })}
              testid="signup-password"
              required
              hint="At least 6 characters"
            />

            {err && (
              <div className="text-sm text-clay-600 border border-clay-600/30 bg-clay-400/10 px-3 py-2" data-testid="signup-error">
                {err}
              </div>
            )}

            <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="signup-submit">
              {loading ? "Creating…" : (<>Create my clinic <ArrowRight size={14} /></>)}
            </button>
          </div>
          <div className="mt-8 text-xs text-forest-400 leading-relaxed">
            By signing up, you agree to our{" "}
            <Link to="/terms" className="text-forest-600 underline underline-offset-2" data-testid="signup-terms-link">Terms</Link>{" "}
            and{" "}
            <Link to="/privacy" className="text-forest-600 underline underline-offset-2" data-testid="signup-privacy-link">Privacy</Link>. We never train
            external AI models on patient photos.
          </div>
        </form>
      </div>

      <div className="hidden md:flex bg-forest-600 text-white relative">
        <div className="m-auto max-w-md px-12 py-16">
          <h2 className="font-heading text-4xl leading-tight">
            Launch your AI smile
            <br />
            preview link today.
          </h2>
          <p className="text-bone-100/70 mt-6 leading-relaxed">
            You'll get a branded <code className="font-mono text-bone-100">/preview/your-clinic</code> page,
            a lead CRM, and an embeddable patient flow, in under 90 seconds if you skip the optional setup. Polish the rest later from your dashboard.
          </p>
          <ul className="mt-10 space-y-3 text-sm text-bone-100/80">
            {[
              "Branded patient capture page",
              "Photo-real AI smile rendering",
              "Lead inbox with status tracking",
              "20 free smile transforms on trial",
            ].map((x) => (
              <li key={x} className="flex items-start gap-2">
                <span className="text-bone-200/70">·</span>
                {x}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, hint, testid }) {
  return (
    <div>
      <label className="label-flat">{label}</label>
      <input
        type={type}
        className="input-flat"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
      />
      {hint && <div className="text-xs text-forest-400 mt-1">{hint}</div>}
    </div>
  );
}
